const BLOGGER_API_KEY = import.meta.env.VITE_BLOGGER_API_KEY;
const PROJECTS_BLOG_ID = import.meta.env.VITE_PROJECTS_BLOG_ID;
const EVENTS_BLOG_ID = import.meta.env.VITE_EVENTS_BLOG_ID;

interface BlogPost {
  id: string;
  title: string;
  content: string;
  published: string;
  updated: string;
  url: string;
  author: {
    displayName: string;
    url: string;
    image?: {
      url: string;
    };
  };
  images?: string[];
}

interface BloggerResponse {
  items: BlogPost[];
  nextPageToken?: string;
}

const extractImages = (content: string): string[] => {
  const imgRegex = /<img[^>]+src="([^">]+)"/g;
  const images: string[] = [];
  let match;

  while ((match = imgRegex.exec(content)) !== null) {
    images.push(match[1]);
  }

  return images;
};

// https://www.googleapis.com/blogger/v3/blogs/1182086866361849215/posts/8130160078874094318

export const getBlogPost = async (postId: string): Promise<BlogPost> => {
  const params = new URLSearchParams({
    key: BLOGGER_API_KEY,
    fetchImages: "true",
  });

  const response = await fetch(
    `https://www.googleapis.com/blogger/v3/blogs/${EVENTS_BLOG_ID}/posts/${postId}?${params}`,
  );

  if (!response.ok) {
    throw new Error("Failed to fetch blog post");
  }

  const responseData = await response.json();

  const processedPost = {
    ...responseData,
    images: extractImages(responseData.content),
  };

  return processedPost;
};

export const fetchBlogPosts = async (
  blogId: string,
  pageToken?: string,
): Promise<BloggerResponse> => {
  try {
    const params = new URLSearchParams({
      key: BLOGGER_API_KEY,
      fetchImages: "true",
      maxResults: "10",
      ...(pageToken && { pageToken }),
    });

    const response = await fetch(
      `https://www.googleapis.com/blogger/v3/blogs/${blogId}/posts?${params}`,
    );

    if (!response.ok) {
      throw new Error("Failed to fetch blog posts");
    }

    const data = await response.json();

    // Process posts to extract images and clean up content
    const processedPosts = data.items.map((post: BlogPost) => ({
      ...post,
      images: extractImages(post.content),
    }));

    return {
      items: processedPosts,
      nextPageToken: data.nextPageToken,
    };
  } catch (error) {
    console.error("Error fetching blog posts:", error);
    throw error;
  }
};

export const fetchProjectPosts = (pageToken?: string) =>
  fetchBlogPosts(PROJECTS_BLOG_ID, pageToken);

export const fetchEventPosts = (pageToken?: string) =>
  fetchBlogPosts(EVENTS_BLOG_ID, pageToken);

export type { BlogPost };
